import * as React from 'react';
import * as ko from 'knockout';
import { endOfMonth, startOfMonth, subDays, subMonths } from 'date-fns';

import {
  AccountingSoftwareBillAttachmentDetails,
  AccountingSoftwareBillFileAttachment,
  FileAttachment,
  FileStorageFileAttachment,
} from '@/features/accounting-software';
import SyncButtonWithModalContainer from '@/features/accounting-software/components/SyncButtonWithModalContainer';
import { Instance as companyAccountingSoftwareService } from '@/features/accounting-software/services/companyAccountingSoftware';
import { FeatureFlag, FeatureToggleService } from '@/features/feature-flags';
import { fileStorageService } from '@/features/file-attachments';
import { FreeTrialSignupBanner } from '@/features/my-payments';
import { PaymentsLog } from '@/features/payments-log';
import titleMessageModal from '@/legacy/modals/styled/titleMessage';
import * as contactsService from '@/legacy/services/contacts';
import { ContactPaymentViewModel } from '@/legacy/services/contacts';
import * as paginationService from '@/legacy/services/pagination';
import { ApiDateFormat, DisplayDateFormat, formatLocalDate } from '@/utils/formatDate';

declareModules({
  'components/user/tables/paginationDisplay': () =>
    import('@/legacy/components/user/tables/paginationDisplay'),
  'components/user/tables/paginationDisplay.html': () =>
    import('@/legacy/components/user/tables/paginationDisplay.html'),
});

interface ITimePeriodOption {
  name: string;
  value: string;
}

export class ViewModel {
  private timePeriod = ko.observable<string>(undefined);

  private timePeriodSubscription: KnockoutSubscription;

  public status = ko.observable<string>('loading');

  public debitCompanyName = ko.observable<string>();

  public creditContactName = ko.observable<string>();

  public payments: contactsService.ContactPaymentCollectionViewModel;

  public companyAccountingSoftwareService = companyAccountingSoftwareService;

  public emailKey = ko.observable<string>();

  public hasAnyAttachments = ko.observable<boolean>(false);

  public pagination: paginationService.PaginationKnockoutModel;

  public isNetworkExperimentEnabled = ko.observable<boolean>(false);

  public isNetworkImprovementEnabled = ko.observable<boolean>(false);

  public isNetworkExperimentFlagLoaded = ko.observable<boolean>(false);

  public shouldRenderLegacyUi = ko.pureComputed(
    () => !(this.isNetworkExperimentEnabled() || this.isNetworkImprovementEnabled())
  );

  private paginationSubscription: KnockoutSubscription;

  public react = {
    PaymentsLog,
    fragments: {
      FreeTrialSignupBanner,
      SyncButtonWithModalContainer,
    },
  };

  constructor() {
    this.pagination = new paginationService.PaginationKnockoutModel();
    this.pagination.resultsPerPage(10);
  }

  public activate(emailKey: string) {
    this.emailKey(emailKey);
  }

  public attached(view: HTMLElement, parent: HTMLElement): void {
    FeatureToggleService.isFeatureEnabledForAnonymous(
      FeatureFlag.SPR7_2024_NETWORK_IMPROVEMENT1_1
    ).then((enabled: boolean) => {
      const queryParams = new URLSearchParams(window.location.hash.split('?')[1]);
      const newUiParamPresent = Boolean(queryParams.get('payeeEmailUniqueKey'));
      const useNewUi = enabled && newUiParamPresent;
      this.isNetworkImprovementEnabled(useNewUi);
      if (useNewUi) {
        this.isNetworkExperimentEnabled(false);
        this.isNetworkExperimentFlagLoaded(true);
      } else {
        $('body').addClass('email-transfer');
        this.timePeriodSubscription = this.timePeriod.subscribe(() => {
          this.pagination.page(0);
          this.updatePayments(0);
        });

        this.paginationSubscription = this.pagination.page.subscribe(() => {
          this.updatePayments(this.pagination.page());
        });

        this.updatePayments(0);
      }
    });
  }

  private updatePayments(page: number) {
    let fromPeriod = null;
    let toPeriod = null;
    if (this.timePeriod()) {
      const periodElements = this.timePeriod().split('/');
      [fromPeriod, toPeriod] = periodElements;
    }

    this.status('loading');

    const contactPaymentsXhr = contactsService.Instance.GetByEmailKey(
      this.emailKey(),
      fromPeriod,
      toPeriod,
      page
    );

    contactPaymentsXhr.dataPromise.then(
      (contactPaymentCollection: contactsService.ContactPaymentCollectionViewModel) => {
        FeatureToggleService.isFeatureEnabled(
          FeatureFlag.SPR6_2024_NETWORK_EXPERIMENT1_1,
          contactPaymentCollection.contactKey
        )
          .then((enabled) => {
            this.isNetworkExperimentEnabled(enabled);
          })
          .finally(() => {
            this.isNetworkExperimentFlagLoaded(true);
          });

        // If we are looking at invalid page we'll go back all the way to the first page
        if (
          this.pagination.totalPages() > 0 &&
          this.pagination.page() >= this.pagination.totalPages()
        ) {
          this.pagination.page(0);
          return;
        }

        for (let i = 0; i < contactPaymentCollection.payments.length; i += 1) {
          if (contactPaymentCollection.payments[i].hasAttachments) {
            this.hasAnyAttachments(true);
            contactPaymentCollection.payments[i].attachments =
              new contactsService.ContactPaymentAttachments();
          }
        }
        this.payments = contactPaymentCollection;

        this.debitCompanyName(contactPaymentCollection.debitCompanyName);
        this.creditContactName(contactPaymentCollection.creditContactName);

        this.pagination.setContentRange(contactPaymentsXhr.contentRange);

        this.status('loaded');
      }
    );
  }

  public detached(): void {
    if (this.timePeriodSubscription) {
      this.timePeriodSubscription.dispose();
    }
    if (this.paginationSubscription) {
      this.paginationSubscription.dispose();
    }
    $('body').removeClass('email-transfer');
  }

  public TimePeriods: KnockoutComputed<Array<any>> = ko.pureComputed({
    read: () => {
      const today = new Date();
      const MAX_MONTHS_BACK = 12;
      const periods: Array<ITimePeriodOption> = [];
      const formatPeriod = (startDate: Date, endDate: Date) => {
        const start = formatLocalDate(startDate, ApiDateFormat.DateOnly);
        const end = formatLocalDate(endDate, ApiDateFormat.DateOnly);
        return `${start}/${end}`;
      };

      periods.push({
        name: 'All',
        value: '',
      });
      periods.push({
        name: 'Last 10 days',
        value: formatPeriod(subDays(today, 10), today),
      });
      periods.push({
        name: 'This Month',
        value: formatPeriod(startOfMonth(today), endOfMonth(today)),
      });
      periods.push({
        name: 'Last 31 Days',
        value: formatPeriod(subDays(today, 31), today),
      });

      for (let i = 0; i < MAX_MONTHS_BACK; i += 1) {
        const month = subMonths(today, i + 1);
        periods.push({
          name: formatLocalDate(month, DisplayDateFormat.CalendarMonth),
          value: formatPeriod(startOfMonth(month), endOfMonth(month)),
        });
      }
      if (!this.timePeriod()) {
        this.timePeriod(periods[0].value);
      }
      return periods;
    },
    deferEvaluation: true,
  });

  public NavigateSignUp() {
    window.location.href =
      'https://www.plooto.com/signup?utm_source=portal&utm_medium=banner_cta&utm_campaign=experiment_1.0&utm_content=control';
  }

  public styleStatus(payment) {
    const statusId = payment.paymentStatusValue;

    if (statusId == 14 /* CREDIT_CLEARING*/) {
      return 'sending';
    }
    if (statusId === 90 || statusId === 100 || statusId === 95) {
      return 'unknown';
    } // On Hold (QUEUE_PAYMENTS, PROCESS_PAYMENTS, WALLET_PREFUNDING_PENDING)
    if (statusId === 4 || (statusId >= 50 && statusId <= 100)) {
      return 'clearing';
    }
    if (statusId == 5) {
      return 'rejected';
    } // Debit failed
    if (statusId == 30) {
      return 'clearing';
    } // Recall Pending

    if (statusId < 10) {
      return 'debiting';
    }

    if (statusId == 13) {
      return 'unknown';
    }
    if (statusId < 20) {
      return 'sending';
    }

    if (statusId == 20 || statusId == 24) {
      return 'pendingApproval';
    }
    if (statusId == 21 || statusId == 22) {
      return 'rejected';
    }
    if (statusId >= 30 && statusId <= 130) {
      return 'clearing';
    }

    if (statusId === 200 /* COMLPETED*/) {
      return 'completed';
    }

    return 'unknown';
  }

  public displayStatus(payment) {
    const statusId = payment.paymentStatusValue;

    if (statusId < 4) {
      return 'Funds Withdrawn';
    }
    if (statusId == 14) {
      return 'Credit Issued';
    } // CREDIT_CLEARING

    if (statusId == 4) {
      return 'Clearing';
    } // DEBIT_CLEARING
    if (statusId == 5) {
      return 'Debit Failed';
    }
    if (statusId == 13) {
      return 'Credit Failed';
    }
    if (statusId == 30) {
      return 'Recall Pending';
    }

    if (statusId <= 10 || (statusId >= 50 && statusId <= 100)) {
      return 'Clearing';
    }
    if (statusId < 20) {
      return 'Issued To Bank';
    }
    if (statusId == 21) {
      return 'Rejected';
    }
    if (statusId == 22) {
      return 'Recalled';
    }
    if (statusId >= 30 && statusId <= 130) {
      return 'Clearing';
    }
    if (statusId === 200) {
      return 'Completed';
    } // COMPLETED

    return 'Unknown';
  }

  public statusDescription(payment): React.JSX.Element {
    const statusId = payment.paymentStatusValue;
    let statusTitle = '';
    let statusInfo = '';

    if (statusId == 14 /* CREDIT_CLEARING*/) {
      statusTitle = 'Credit Issued';
      statusInfo =
        'The payment was sent to your bank. Depending on the bank, it can take them 1-3 business days to reflect the credit in your bank account.';
    } else if (statusId < 4) {
      statusTitle = 'Funds Withdrawn';
      statusInfo =
        'Instructions have been issued for the funds to be debited from the senders bank account';
    } else if (statusId === 4) {
      statusTitle = 'Clearing';
      statusInfo =
        'The funds are being validated and processed as per standard banking regulations.';
    } else if (statusId === 5) {
      statusTitle = 'Debit Failed';
      statusInfo = 'Funds could not be debited';
    } else if (statusId === 13) {
      statusTitle = 'Credit Failed';
      statusInfo = 'Funds could not be credited';
    } else if (statusId == 70) {
      statusTitle = 'Awaiting Acceptance';
      statusInfo =
        'Transfer started. Your funds will be credited to the payee once they accept the email money transfer.';
    } else if (statusId < 20) {
      statusTitle = 'Issued to bank';
      statusInfo =
        'The funds have been credited to your bank and will be reflected in your account after they process the transaction';
    } else if (statusId == 20 || statusId == 24) {
      statusTitle = 'Approval Required';
      statusInfo = 'Approval required to proceed';
    } else if (statusId == 21) {
      statusTitle = 'Rejected';
      statusInfo = 'Payment has been rejected';
    } else if (statusId == 22) {
      statusTitle = 'Recalled';
      statusInfo = 'Payment has been recalled';
    } else if (statusId == 30) {
      statusTitle = 'Recall Pending';
      statusInfo = 'Payment has been recalled and will not be credited.';
    } else if (statusId >= 30 && statusId <= 130) {
      statusTitle = 'Processing';
      statusInfo =
        'Funds are currently in transit and estimated to arrive on "Estimated Credit Date".';
    } else if (statusId === 200 /* COMLPETED*/) {
      statusTitle = 'Completed';
      statusInfo =
        'The payment was sent to your bank. Depending on the bank, it can take them 1-3 business days to reflect the credit in your bank account.';
    } else if (statusId >= 50 && statusId <= 100) {
      statusTitle = 'Processing';
      statusInfo =
        'Funds are currently in transit and estimated to arrive on "Estimated Credit Date".';
    }

    const statusDescription: React.JSX.Element = statusInfo
      ? React.createElement(React.Fragment, null, [
          React.createElement('strong', null, [statusTitle]),
          React.createElement('br'),
          statusInfo,
        ])
      : React.createElement('span', null, [statusTitle]);
    return statusDescription;
  }

  public GetPaymentAttachments(contactPaymentViewModel: contactsService.ContactPaymentViewModel) {
    const paymentAttachments: contactsService.ContactPaymentAttachments =
      contactPaymentViewModel.attachments;

    if (
      paymentAttachments.attachmentStatus() == 'loadRequired' ||
      paymentAttachments.attachmentStatus() == 'error'
    ) {
      paymentAttachments.attachmentStatus('loading');
      contactsService.Instance.GetPaymentAttachmentsForContact(
        this.emailKey(),
        contactPaymentViewModel.id
      )
        .then((attachments: Array<AccountingSoftwareBillAttachmentDetails>) => {
          paymentAttachments.attachments(attachments);
          paymentAttachments.attachmentStatus('loaded');
        })
        .fail(() => {
          paymentAttachments.attachmentStatus('error');
        });
    }
  }

  public async viewPaymentAttachment(
    payment: ContactPaymentViewModel,
    fileAttachment: FileAttachment
  ) {
    switch (fileAttachment.discriminator) {
      case 'AccountingSoftwareBillFileAttachment': {
        const accountingSoftwareBillFileAttachment =
          fileAttachment as AccountingSoftwareBillFileAttachment;
        contactsService.Instance.openWindow(accountingSoftwareBillFileAttachment.url);
        break;
      }
      case 'FileStorageFileAttachment': {
        const fileStorageFileAttachment = fileAttachment as FileStorageFileAttachment;
        try {
          const fileStorageDownloadLink =
            await contactsService.Instance.generateFileStorageDownloadLink(
              this.emailKey(),
              payment.id,
              fileStorageFileAttachment.companyFileStorageFileId
            );

          fileStorageService.viewFileStorageFile(fileStorageDownloadLink);
        } catch (err) {
          const getErrorMessage = () => {
            switch (err.type) {
              case 'invalid.attachmentsVisibleToContacts':
                return {
                  title: 'Failed downloading attachment',
                  message:
                    'This company has disabled ability to view attachments. Please contact sender.',
                };

              default:
                return {
                  title: 'Failed downloading attachment',
                  message:
                    'There was an error downloading this file attachment. Please try again later.',
                };
            }
          };
          const { title, message } = getErrorMessage();

          titleMessageModal.show({
            title,
            message,
            svgIconSymbolLarge: 'svg-warning-large',
          } as any);
          console.log(err);
        }
        break;
      }
    }
  }
}
